import { useMemo } from 'react';
import {
	flowDateSlots,
	flowSignatureSlots,
} from '@dealstack/shared/lib/common/business/documentFlow';
import {
	NO_COHORTS,
	NO_DATE_ANCHORS,
	NO_INVESTOR_PREVIEW,
	NO_SIGNATURE_SLOTS,
} from '@dealstack/frontend/shared/components/FieldBinding/DocxFieldBinding/docxFlowEditor.adapter.ts';
import type {
	DocxDateAnchorsResult,
	DocxFlowEditorAdapter,
	DocxSignatureSlotsResult,
} from '@dealstack/frontend/shared/components/FieldBinding/DocxFieldBinding/docxFlowEditor.adapter.ts';
import {
	useGlobalDocxFlowDocumentQuery,
	useSaveGlobalDocxFlowDocument,
} from '@dealstack/frontend/app/pages/DocumentTemplates/api/useGlobalDocxFlowDocument';
import { useGlobalDocxTemplateRender } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useGlobalDocxTemplateRender';
import { useGlobalDataPointCatalogueQuery } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useGlobalDataPointCatalogueQuery';

/**
 * The Word editor, wired to a global library template. Module-level: its members are hooks.
 * Signatures, dates, investor preview and cohorts are constants — a library template has no
 * signing roles, post-dating flag, investors or programme.
 */
export const globalDocxFlowAdapter: DocxFlowEditorAdapter = {
	useFlowDocument: (templateId) => useGlobalDocxFlowDocumentQuery(templateId),
	useSaveFlowDocument: () => useSaveGlobalDocxFlowDocument(),
	// Investor ignored: the catalogue is sample-resolved and identical for every library template.
	useCatalogue: (templateId) => useGlobalDataPointCatalogueQuery(templateId),
	useRender: () => useGlobalDocxTemplateRender(),
	/** An anchor can still arrive by paste, and the server refuses it — so block Save here
	 *  rather than 400 after the whole document is written. */
	useSignatureSlots: (_templateId, document) =>
		useMemo<DocxSignatureSlotsResult>(() => {
			const pasted = document === null ? [] : flowSignatureSlots(document);
			if (pasted.length === 0) {
				return NO_SIGNATURE_SLOTS;
			}
			return {
				...NO_SIGNATURE_SLOTS,
				isComplete: false,
				duplicatedLabels: ['A signature, which a library template cannot carry'],
			};
		}, [document]),
	/** The same for a date anchor, which nothing in the library can ever date. */
	useDateAnchors: (_templateId, document) =>
		useMemo<DocxDateAnchorsResult>(() => {
			const pasted = document === null ? [] : flowDateSlots(document);
			if (pasted.length === 0) {
				return NO_DATE_ANCHORS;
			}
			return {
				...NO_DATE_ANCHORS,
				isComplete: false,
				duplicatedLabels: ['A date field, which a library template cannot carry'],
			};
		}, [document]),
	useInvestorPreview: () => NO_INVESTOR_PREVIEW,
	// A constant, like its siblings: `() => []` allocates per render, and cohorts are threaded
	// into three memo dependency lists on the most render-sensitive surface in the editor.
	useCohorts: () => NO_COHORTS,
};
